// ------------- import external dependencies --------------
import moment from "moment";
import { instance } from "../instance";

/**
 * Incident related APIs
 * @returns
 */

export const getIncidentTypes = async () => {
  return instance({
    method: "get",
    url: `/incident/types`,
  });
};

/**
 * Get available skin issue types for wound analysis
 * Kept here to match incident API grouping and reuse
 */
export const getSkinIssueTypes = async () => {
  return instance({
    method: "get",
    url: `/incident/skin-issue-types`,
  });
};

/**
 * Get available behavior types for behavior analysis report
 */
export const getBehaviorIncidentSubTypes = async () => {
  return instance({
    method: "get",
    url: `/incident/incident-sub-types`,
  });
};

/**
 * Get available target of aggression types for behavior analysis report
 */
export const getTargetOfAggressionTypes = async () => {
  return instance({
    method: "get",
    url: `/incident/target-of-aggression-types`,
  });
};

/**
 * Get available location unit types for behavior analysis report
 */
export const getLocationUnitTypes = async (selectedFacility?: string) => {
  return instance({
    method: "get",
    url: `/incident/location-unit-types?facilityId=${selectedFacility}`,
  });
};

/**
 * Create Incident Entry
 */
export const createIncident = async (data: any, idempotencyKey?: string) => {
  return instance({
    method: "post",
    url: idempotencyKey
      ? `/incident?idempotencyKey=${idempotencyKey}`
      : `/incident`,
    data,
  });
};

export const setIncidentStatusToOpen = async (data: any) => {
  return instance({
    method: "put",
    url: `/incident`,
    data,
  });
};

/**
 * Mark incident as Ready for Approval
 */
export const markIncidentReadyForApproval = async (incidentId: string) => {
  return instance({
    method: "put",
    url: `/incident/mark-ready-for-approval`,
    data: { incidentId },
  });
};

/**
 * Get created Incidents
 */

export const getIncidentsWithParams = async ({
  userId,
  startDate,
  endDate,
}: any) => {
  let url: string = `/search/incident/list/type/?userId=${userId}`;
  if (startDate?.length && endDate?.length) {
    url += `&startDate=${moment(startDate).format("yyyy-MM-DD")}&endDate=${moment(endDate).format("yyyy-MM-DD")}`;
  }
  if (
    !startDate?.length &&
    !endDate?.length
  ) {
    url = `/incident/list/?userId=${userId}`;
  }
  return instance({
    method: "get",
    url,
  });
};

export const getSelectedIncidentWithId = async (incidentId: string) => {
  return instance({
    method: "get",
    url: `/incident/details/?incidentId=${incidentId}`,
  });
};

export const getdraftIncidentWithId = async (draftId: string) => {
  return instance({
    method: "get",
    url: `/draft/incident/details/?draftId=${draftId}`,
  });
};

export const getIncidentById = async (incidentId: string) => {
  return instance({
    method: "get",
    url: `incident/details/?incidentId=${incidentId}`,
  });
};

/**
 * Get Users in Facility by userType and facilityId
 */

export enum UserType {
  staff = "staff",
  resident = "resident",
  caregiver = "caregiver",
  provider = "provider",
}

export const getUsersInFacility = async (
  facilityId: string,
  userType: UserType
) => {
  return instance({
    method: "get",
    url: `users_in_facility?userType=${userType}&facilityId=${facilityId}`,
  });
};

export const getAllPickListOptions = async () => {
  return instance({
    method: "get",
    url: `/getAllPicklist`,
  });
};

/**
 * Save and retrieve incident form data as draft
 */

export const saveIncidentFormData = async (
  data: any,
  idempotencyKey: string
) => {
  return instance({
    method: "post",
    url: `/drafts?idempotencyKey=${idempotencyKey}`,
    data,
  });
};

export const getDeletedIncidents = async ({
  user,
  facilityId,
  incidentType,
  startDate,
  endDate,
}: any) => {
  let url = `/deleted_incident/?userId=${user}`;
  if (facilityId?.length) url += `&facilityId=${facilityId}`;
  if (incidentType?.length) url += `&incidentType=${incidentType}`;
  if (startDate?.length && endDate?.length) {
    url += `&startDate=${moment(startDate).format("yyyy-MM-DD")}&endDate=${moment(endDate).format("yyyy-MM-DD")}`;
  }
  return instance({
    method: "get",
    url,
  });
};

export const restoreDeletedIncident = async (
  incidentId: string,
  userId: string
) => {
  return instance({
    method: "put",
    url: `/deleted_incident/?incidentId=${incidentId}&loggedInUserId=${userId}`,
  });
};

export const getSavedIncidentDrafts = async ({
  userId,
  startDate,
  endDate,
}: any) => {
  let url = `/draft/list?userId=${userId}`;
  if (startDate?.length && endDate?.length) {
    url += `&startDate=${moment(startDate).format("yyyy-MM-DD")}&endDate=${moment(endDate).format("yyyy-MM-DD")}`;
  }
  return instance({
    method: "get",
    url,
  });
};

export const deleteIncidentFormData = async (
  data: any,
  idempotencyKey: string
) => {
  return instance({
    method: "delete",
    url: `/drafts?idempotencyKey=${idempotencyKey}`,
    data,
  });
};

export const getAllIncidentSignatories = async (incidentId: string) => {
  return instance({
    method: "get",
    url: `/signatures?incidentId=${incidentId}`,
  });
};

export const addIncidentSignatory = async (data: any) => {
  return instance({
    method: "put",
    url: `/signatures`,
    data,
  });
};

export const resetIncidentSignatory = async (signatureId: string) => {
  return instance({
    method: "post",
    url: `/signatures?signatureId=${signatureId}`,
  });
};

/**
 * Get incidents by trends data
 * @param facilityId
 * @param month
 * @param year
 * @param incidentType
 */
export const getIncidentListByTrends = async (
  month: string,
  year: string,
  incidentTypes: string,
  facilityIds: string
) => {
  return instance({
    method: "get",
    url: `/analytic/trend/list?month=${month}&year=${year}&facility=${facilityIds}&incidentType=${incidentTypes}`,
  });
};

/**
 * Post attachments to an incident using :id
 * @param incidentId
 */
export const handlePostIncidentAttachment = async (
  data: any,
  incidentId: string
) => {
  return instance({
    method: "post",
    url: `/attachments?incidentId=${incidentId}`,
    data,
  });
};

/**
 * Get attachments using incident :id
 * @param incidentId
 */
export const getSingleAttachments = async (attachmentId: string) => {
  return instance({
    method: "get",
    url: `/attachment?attachmentId=${attachmentId}`,
  });
};

/**
 * Get attachments using incident :id
 * @param incidentId
 */
export const getAttachments = async (incidentId: string) => {
  return instance({
    method: "get",
    url: `/attachments?incidentId=${incidentId}`,
  });
};

/**
 * Delete attachments using attachment :id
 * @param attachmentId
 */
export const deleteAttachment = async (data: any, attachmentId: string) => {
  return instance({
    method: "delete",
    url: `/attachments?attachmentId=${attachmentId}`,
    data,
  });
};

export const deleteIncidentEndpoint = async ({
  id,
  loggedInUserId,
}: {
  id: string;
  loggedInUserId: string;
}) => {
  return instance({
    method: "delete",
    url: `/incident?incidentId=${id}&loggedInUserId=${loggedInUserId}`,
  });
};

export const deleteDraftIncidentFromListEndPoint = async (data: any) => {
  return instance({
    method: "delete",
    url: `/drafts?idempotencyKey=${data}`,
    data,
  });
};
